import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Printer, X, CheckCircle, Package, Bell, XCircle, Bluetooth, ForkKnife } from "@phosphor-icons/react";
import { smartPrint } from "@/lib/smartPrint";
import { subscribePrinterState, getState as getBtState } from "@/lib/btThermal";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { api, authHeaders } from "@/lib/api";

const STATUS_FLOW = [
    { id: "preparing", label: "Preparando", icon: Package, color: "bg-secondary text-secondary-foreground" },
    { id: "ready", label: "Listo", icon: Bell, color: "bg-blue-500 text-white" },
    { id: "delivered", label: "Entregado", icon: CheckCircle, color: "bg-emerald-600 text-white" },
    { id: "cancelled", label: "Cancelar", icon: XCircle, color: "bg-red-500 text-white" },
];

export default function OrderDetailDialog({ order, open, onClose, onStatusUpdate, onRefunded }) {
    const [btState, setBtState] = useState(getBtState());
    useEffect(() => subscribePrinterState(setBtState), []);
    const [refundOpen, setRefundOpen] = useState(false);
    const [refundAmt, setRefundAmt] = useState("");
    const [refundReason, setRefundReason] = useState("");
    const [refundBusy, setRefundBusy] = useState(false);

    if (!order) return null;

    const refunded = Number(order.refund_amount || 0);
    const remaining = +(Number(order.total || 0) - refunded).toFixed(2);
    const submitRefund = async () => {
        const amt = refundAmt === "" ? null : parseFloat(refundAmt);
        if (amt !== null && !(amt > 0 && amt <= remaining + 0.001)) {
            toast.error(`Monto inválido (máx $${remaining.toFixed(2)})`);
            return;
        }
        setRefundBusy(true);
        try {
            const r = await api.post(`/admin/orders/${order.id}/refund`,
                { amount: amt, reason: refundReason }, { headers: authHeaders() });
            toast.success(`Reembolso registrado: $${(amt ?? remaining).toFixed(2)}`);
            setRefundOpen(false); setRefundAmt(""); setRefundReason("");
            onRefunded?.(r.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "No se pudo registrar el reembolso");
        } finally {
            setRefundBusy(false);
        }
    };

    const printCustomer = () => smartPrint(order, { kind: "customer" });
    const printKitchen = () => smartPrint(order, { kind: "kitchen" });

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent
                data-testid="order-detail-dialog"
                className="max-w-lg max-h-[90vh] overflow-y-auto"
            >
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl font-black tracking-tight">
                        Order #{order.id.slice(0, 8)}
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-4 text-sm">
                    {/* Status update buttons */}
                    {onStatusUpdate && (
                        <div data-testid="order-status-buttons" className="space-y-2">
                            <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
                                Cambiar estado · actual:{" "}
                                <span className="text-foreground">{order.status}</span>
                            </p>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                {STATUS_FLOW.map((s) => {
                                    const Icon = s.icon;
                                    const isCurrent = order.status === s.id;
                                    return (
                                        <button
                                            key={s.id}
                                            data-testid={`status-${s.id}-${order.id}`}
                                            onClick={() => onStatusUpdate(order.id, s.id)}
                                            disabled={isCurrent}
                                            className={`flex items-center justify-center gap-1.5 py-3 rounded-full font-bold uppercase tracking-widest text-[11px] transition hover:-translate-y-0.5 disabled:opacity-40 disabled:cursor-not-allowed disabled:translate-y-0 ${s.color}`}
                                        >
                                            <Icon size={14} weight="bold" />
                                            {s.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    <div className="grid grid-cols-2 gap-3">
                        <Info label="Customer" value={order.customer_name} />
                        <Info label="Phone" value={order.customer_phone} />
                        <Info label="Email" value={order.customer_email || "—"} />
                        <Info label="Fulfillment" value={order.fulfillment} />
                        <Info label="Status" value={order.status} />
                        <Info label="Payment" value={order.payment_status} />
                    </div>

                    {order.address && <Info label="Address" value={order.address} block />}
                    {order.notes && <Info label="Notes" value={order.notes} block />}

                    <div>
                        <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">
                            Items
                        </p>
                        <ul className="divide-y divide-border bg-muted/40 rounded-xl">
                            {order.items.map((it, i) => (
                                <li key={i} className="flex justify-between p-3">
                                    <span>
                                        {it.qty}× {it.name}
                                    </span>
                                    <span className="font-semibold">${it.line_total.toFixed(2)}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="bg-foreground text-background rounded-xl p-4 space-y-1">
                        <Row label="Subtotal" value={`$${order.subtotal.toFixed(2)}`} />
                        {order.discount > 0 && <Row label="Discount" value={`-$${order.discount.toFixed(2)}`} />}
                        <Row label="Delivery" value={`$${order.delivery_fee.toFixed(2)}`} />
                        <Row label={`Tax${order.tax_rate ? ` ${(order.tax_rate * 100).toFixed(2)}%` : ""}`} value={`$${order.tax.toFixed(2)}`} />
                        {order.tip > 0 && <Row label="Tip" value={`$${order.tip.toFixed(2)}`} />}
                        <Row label="Total" value={`$${order.total.toFixed(2)}`} big />
                        {refunded > 0 && (
                            <p data-testid="order-refunded" className="text-xs text-red-300 font-bold pt-1">
                                Refunded: -${refunded.toFixed(2)} ({order.refund_status})
                            </p>
                        )}
                    </div>

                    {order.payment_status === "paid" && remaining > 0 && (
                        <div data-testid="refund-box" className="rounded-xl border border-red-200 bg-red-50 p-3 space-y-2">
                            {!refundOpen ? (
                                <button
                                    type="button"
                                    data-testid="refund-open-btn"
                                    onClick={() => setRefundOpen(true)}
                                    className="text-xs font-bold uppercase tracking-widest text-red-700 hover:underline"
                                >
                                    Registrar reembolso (máx ${remaining.toFixed(2)})
                                </button>
                            ) : (
                                <>
                                    <div className="grid grid-cols-2 gap-2">
                                        <Input
                                            data-testid="refund-amount-input"
                                            type="number" min="0.01" step="0.01" max={remaining}
                                            placeholder={`Total $${remaining.toFixed(2)}`}
                                            value={refundAmt}
                                            onChange={(e) => setRefundAmt(e.target.value)}
                                            className="h-9 bg-white"
                                        />
                                        <Input
                                            data-testid="refund-reason-input"
                                            placeholder="Motivo"
                                            value={refundReason}
                                            onChange={(e) => setRefundReason(e.target.value)}
                                            className="h-9 bg-white"
                                        />
                                    </div>
                                    <div className="flex gap-2">
                                        <Button
                                            data-testid="refund-submit-btn"
                                            disabled={refundBusy}
                                            onClick={submitRefund}
                                            className="rounded-full h-9 bg-red-600 hover:bg-red-700 text-white text-xs font-bold uppercase tracking-widest"
                                        >
                                            {refundAmt === "" ? "Reembolso total" : "Reembolsar"}
                                        </Button>
                                        <Button variant="outline" onClick={() => setRefundOpen(false)} className="rounded-full h-9 text-xs">
                                            Cancelar
                                        </Button>
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    <p className="text-xs text-muted-foreground">
                        Created {new Date(order.created_at).toLocaleString()}
                    </p>

                    <div className="pt-2 space-y-2">
                        {/* Bluetooth status pill */}
                        <div className="flex items-center justify-between text-xs px-1">
                            <span className="inline-flex items-center gap-1.5">
                                <Bluetooth
                                    size={12}
                                    weight="bold"
                                    className={btState.connected ? "text-blue-500" : "text-muted-foreground/50"}
                                />
                                <span className="uppercase tracking-widest font-bold text-muted-foreground">
                                    {btState.connected
                                        ? `Bluetooth · ${btState.deviceName || "impresora"}`
                                        : "Bluetooth no conectado"}
                                </span>
                            </span>
                            {!btState.connected && (
                                <a
                                    href="/admin/printer-settings"
                                    className="text-primary font-bold uppercase tracking-widest text-[10px] hover:underline"
                                >
                                    Configurar →
                                </a>
                            )}
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <Button
                                data-testid="print-receipt"
                                onClick={printCustomer}
                                className="rounded-full bg-primary hover:bg-primary/90 font-bold uppercase tracking-widest"
                            >
                                {btState.connected
                                    ? <Bluetooth size={16} weight="bold" className="mr-2" />
                                    : <Printer size={16} weight="bold" className="mr-2" />}
                                Imprimir recibo
                            </Button>
                            <Button
                                data-testid="print-kitchen"
                                onClick={printKitchen}
                                variant="outline"
                                className="rounded-full font-bold uppercase tracking-widest"
                            >
                                <ForkKnife size={16} weight="bold" className="mr-2" />
                                Ticket cocina
                            </Button>
                        </div>

                        <Button
                            variant="ghost"
                            onClick={onClose}
                            data-testid="close-order-detail"
                            className="w-full rounded-full text-muted-foreground"
                        >
                            <X size={14} weight="bold" className="mr-1.5" />
                            Cerrar
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function Info({ label, value, block }) {
    return (
        <div className={block ? "col-span-2" : ""}>
            <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
                {label}
            </p>
            <p className="font-semibold mt-0.5 break-words">{value}</p>
        </div>
    );
}

function Row({ label, value, big }) {
    return (
        <div className="flex justify-between items-center">
            <span className={big ? "font-display font-black" : "text-xs uppercase tracking-widest opacity-70"}>
                {label}
            </span>
            <span className={big ? "font-display font-black text-2xl" : "font-semibold"}>
                {value}
            </span>
        </div>
    );
}
